import {
    Component,
    OnDestroy,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { RouterLink } from "@angular/router";
import { ButtonComponent } from "@components/button/button.component";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { IconComponent } from "@components/icon/icon.component";
import { InfoComponent } from "@components/info/info.component";
import { SelectorComponent } from "@components/selector/selector.component";
import { NgbTooltip } from "@ng-bootstrap/ng-bootstrap";
import groupBy from "lodash/groupBy";
import remove from "lodash/remove";
import sortBy from "lodash/sortBy";
import toPairs from "lodash/toPairs";
import { Observable, Subject, firstValueFrom, of } from "rxjs";
import { concatMap, takeUntil } from "rxjs/operators";
import { BusyService } from "~app/common/components/busy/busy.service";
import { IMessageModel } from "~app/common/components/message-panel/message-panel.models";
import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { SelectionEventType } from "~app/common/components/tree-selector/SelectionEventType";
import { Anskaffningsformer, Atgardstyp, ConfirmDialogResponse } from "~app/common/models/constants";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { CommonTexts } from "~app/common/texts/common.texts";
import { DateUtils as dateUtils } from "~app/common/utils/date-utils";
import { ArbetsorderDisabledFlag } from "~app/features/planera/model/ArbetsorderDisabledFlag";
import { IArbetsorderHandelssortiment } from "~app/features/planera/model/IArbetsorderHandelssortiment";
import { IArbetsorderHandelssortimentsgrupp } from "~app/features/planera/model/IArbetsorderHandelssortimentsgrupp";
import { ValidationMessageRowComponent } from "../../../common/validation/validation-message-row/validation-message-row.component";
import { AddArbetsorderHandelssortimentCommand } from "../commands/AddArbetsorderHandelssortimentCommand";
import { AddArbetsorderProduktCommand } from "../commands/AddArbetsorderProduktCommand";
import { AddArbetsorderProduktinstruktionCommand } from "../commands/AddArbetsorderProduktinstruktionCommand";
import { ArbetsorderHandelssortimentBaseCommand } from "../commands/ArbetsorderHandelssortimentBaseCommand";
import { ArbetsorderProduktBaseCommand } from "../commands/ArbetsorderProduktBaseCommand";
import { ArbetsorderProduktinstruktionBaseCommand } from "../commands/ArbetsorderProduktinstruktionBaseCommand";
import { DeleteArbetsorderHandelssortimentCommand } from "../commands/DeleteArbetsorderHandelssortimentCommand";
import { DeleteArbetsorderProduktCommand } from "../commands/DeleteArbetsorderProduktCommand";
import { DeleteArbetsorderProduktinstruktionCommand } from "../commands/DeleteArbetsorderProduktinstruktionCommand";
import { SetVardekapCommand } from "../commands/SetVardekapCommand";
import { IArbetsorderProdukt } from "../model/IArbetsorderProdukt";
import { IArbetsorderProduktInstruktion } from "../model/IArbetsorderProduktInstruktion";
import { ArbetsorderWrapper } from "../planera-detail/planera-detail-models";
import { ProduktvalComponent } from "../produktval/produktval.component";
import {
    IHandelssortiment,
    IProdukt,
    IProduktInstruktion,
    IProduktvalRow,
    ProduktSelection,
    ProduktvalEvent
} from "../produktval/produktval.models";
import { ArbetsorderMediatorService } from "../services/arbetsorder-mediator.service";
import { ArbetsorderMonitor, ArbetsorderMonitorService } from "../services/arbetsorder-monitor.service";
import { ArbetsorderProduktvalProduktionsmallComponent } from "./arbetsorder-produktval-produktionsmall/arbetsorder-produktval-produktionsmall.component";
import { ArbetsorderProduktvalSummaryComponent } from "./arbetsorder-produktval-summary/arbetsorder-produktval-summary.component";
import { IProduktionsmall, Produktinstruktion } from "./arbetsorder-produktval.models";
import { ArbetsorderProduktvalService } from "./arbetsorder-produktval.service";
import { ArbetsorderProduktvalTexts } from "./arbetsorder-produktval.texts";

@Component({
    selector: "rask-arbetsorder-produktval",
    styleUrls: ["./arbetsorder-produktval.component.scss"],
    templateUrl: "./arbetsorder-produktval.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        SelectorComponent,
        FormsModule,
        RouterLink,
        CheckboxComponent,
        ProduktvalComponent,
        ArbetsorderProduktvalSummaryComponent,
        ButtonComponent,
        IconComponent,
        NgbTooltip,
        ArbetsorderProduktvalProduktionsmallComponent,
        InfoComponent,
        ValidationMessageRowComponent
    ]
})
export class ArbetsorderProduktvalComponent implements OnInit, OnDestroy {
    protected arbetsorderWrapper: ArbetsorderWrapper;
    protected texts = ArbetsorderProduktvalTexts.texts;
    protected commonTexts = CommonTexts.texts;
    protected handelssortimentsgruppProduktvalsrader: { [key: string]: IProduktvalRow[] };
    protected handelssortimentsgrupper: IArbetsorderHandelssortimentsgrupp[];
    protected isFormValid: boolean;
    protected produktionsmallar: IProduktionsmall[];
    protected produktonsmallId: number;
    protected produktsattReminder: IMessageModel;
    protected senastSimulerad: string;
    protected senastVolymhamtad: string;
    protected valdaproduktrader: IProduktvalRow[] = [];
    protected visaEndastMedProduktionsmal = true;

    private readonly arbetsorderMediatorService = inject(ArbetsorderMediatorService);
    private readonly arbetsorderMonitorService = inject(ArbetsorderMonitorService);
    private readonly arbetsorderProduktvalService = inject(ArbetsorderProduktvalService);
    private readonly authenticationService = inject(AuthenticationService);
    private readonly busyService = inject(BusyService);
    private readonly monitor: ArbetsorderMonitor;
    private readonly ngUnsubscribe$ = new Subject<void>();
    private readonly raskModalService = inject(RaskModalService);

    private isParentFormDirty: boolean;

    constructor() {
        this.arbetsorderProduktvalService.produktionsMallar$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(x => {
                this.produktionsmallar = x;
            });

        this.arbetsorderProduktvalService.senastUtbytessimulerad$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(x => {
                if (x && x.tidpunkt) {
                    this.senastSimulerad = dateUtils.formatDateString(x.tidpunkt, "YYYY-MM-DD  HH:mm");
                } else {
                    this.senastSimulerad = "-";
                }
            });

        this.arbetsorderProduktvalService.senastVolymhamtad$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(x => {
                if (x && x.tidpunkt) {
                    this.senastVolymhamtad = dateUtils.formatDateString(x.tidpunkt, "YYYY-MM-DD  HH:mm");
                } else {
                    this.senastVolymhamtad = "-";
                }
            });

        this.monitor = this.arbetsorderMonitorService.connect();
        this.setupValidation();
    }

    protected get isEgenskog(): boolean {
        return this.arbetsorderWrapper && this.arbetsorderWrapper?.arbetsorder.anskaffningsform === Anskaffningsformer.Egenskog;
    }

    protected get isEgenskogOrVirkeskopWithAllowHSGroups(): boolean {
        return this.isEgenskog || this.isVirkeskop;
    }

    protected get isProduktsattValid() {
        return this.arbetsorderWrapper?.arbetsorder &&
            this.arbetsorderWrapper?.arbetsorder.handelssortiment &&
            this.arbetsorderWrapper?.arbetsorder.handelssortiment.length > 0 &&
            this.valdaproduktrader.length > 0 &&
            this.valdaproduktrader.every(produktval => produktval.produktinstruktioner.length > 0);
    }

    protected get isProduktvalApplicable(): boolean {
        return this.arbetsorderWrapper && this.arbetsorderProduktvalService.isProduktvalApplicable(this.arbetsorderWrapper?.arbetsorder);
    }

    protected get isSimuleringApplicable(): boolean {
        return this.arbetsorderWrapper && this.arbetsorderProduktvalService.isSimuleringApplicable(this.arbetsorderWrapper?.arbetsorder);
    }

    protected get isVardekapApplicable() {
        return this.arbetsorderWrapper?.arbetsorder?.isAvverkning === true;
    }

    protected get isVirkeskop(): boolean {
        return this.arbetsorderWrapper && Anskaffningsformer.isVirkesKop(this.arbetsorderWrapper?.arbetsorder.anskaffningsform);
    }

    ngOnDestroy(): void {
        this.monitor.disconnect();
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    async ngOnInit() {
        this.arbetsorderMediatorService.arbetsorder
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(changeEvent => {
                if (!this.arbetsorderWrapper?.arbetsorder || changeEvent?.isUpdated()) {
                    this.arbetsorderWrapper = changeEvent.arbetsorderWrapper;
                    this.init();
                    this.fetchHandelssortimentsgrupper();
                    this.valdaproduktrader = this.arbetsorderProduktvalService.produktvalRowsFromArbetsorder(changeEvent.arbetsorderWrapper);
                    this.setHandelssortimentsgruppProduktvalsraderFromValdaProduktrader();
                }
            });

        this.isFormValid = true;

        this.arbetsorderMediatorService.dirty
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(dirty => {
                this.isParentFormDirty = dirty;
            });
    }

    protected canEdit = () => this.authenticationService.canEdit() && this.arbetsorderWrapper?.arbetsorder?.isEditableForMode(ArbetsorderDisabledFlag.makulerad);

    protected canSimulateAtgardstyp(): boolean {
        return this.arbetsorderWrapper?.arbetsorder?.atgarder?.some(atg => atg.atgardstyp !== Atgardstyp.SkadaPlockhuggning);
    }

    protected hamtaVirkeskostnader() {
        if (!this.isParentFormDirty) {
            this.internalHamtaVirkeskostnader();
        } else {
            this.raskModalService.confirm(new ModalSettings(this.texts.confirmHeaderVirkeskop, this.texts.confirmInfoTextVirkeskop)).subscribe((response) => {
                if (response === ConfirmDialogResponse.save) {
                    this.internalHamtaVirkeskostnader();
                }
            });
        }
    }

    protected onBeforeSelectionEvent = async ($event: ProduktvalEvent): Promise<boolean> => {
        if (this.isSimuleringApplicable) {
            return true;
        }

        const pin = $event.row.produktinstruktion;
        if (pin) {
            return true;
        }

        const hs = $event.row.handelssortiment;
        const prod = $event.row.produkt;

        let matchedRow: IProduktvalRow;
        if (!prod) {
            const rowsForHandelssortiment = this.valdaproduktrader
                .filter(x => x.handelssortimentId === hs.handelssortimentId);
            matchedRow = rowsForHandelssortiment.find(x => !!x.simuleradKvantitet);
        } else {
            matchedRow = this.valdaproduktrader.filter(this.handelssortimentComparer(hs, prod))[0];
        }

        if (!matchedRow || !matchedRow.simuleradKvantitet) {
            return true;
        }

        const message = `Du är på väg att ta bort produkten: ${matchedRow.produkt} som har kvantitet ${matchedRow.simuleradKvantitet}. ` +
            "Om du vill behålla kvantiteten måste du manuellt justera in den på en annan produkt.";
        try {
            const result = await firstValueFrom(this.raskModalService
                .confirm(new ModalSettings(this.texts.borttagProduktKvantitetHeader, message)));
            return result === ConfirmDialogResponse.save;
            // eslint-disable-next-line sonarjs/no-ignored-exceptions
        } catch (err) {
            return false;
        }
    };

    protected onProduktionsMallAddedEvent(): void {
        this.arbetsorderProduktvalService.refreshproduktionsMallar();
    }

    protected async onProduktionsmallChanged(mall: IProduktionsmall): Promise<void> {
        if (mall === null || mall === undefined) {
            return;
        }

        const shouldApplyMall = await this.checkAndConfirmMallUsage(mall);
        if (!shouldApplyMall) {
            return;
        }

        if (this.isVirkeskop) {
            await this.applyProduktionsMallOnVKAO(mall);
        } else {
            await this.applyProduktionsMallOnEgenSkog(mall);
        }
    }

    protected onSelectionEvent($event: ProduktvalEvent) {
        this.handleSelectionEvent($event, false);
    }

    protected produktsattUtfortChanged(value: boolean) {
        this.arbetsorderWrapper?.arbetsorder.setProduktsatt(value);
    }

    protected selectionFormatter = (item: IProduktionsmall) => item;

    protected simulera(): void {
        if (!this.isParentFormDirty) {
            this.arbetsorderMediatorService.utbytesSimulera(false);
        } else {
            this.raskModalService.confirm(new ModalSettings(this.texts.confirmHeader, this.texts.confirmInfoText)).subscribe((response) => {
                if (response === ConfirmDialogResponse.save) {
                    this.arbetsorderMediatorService.utbytesSimulera(true);
                }
            });
        }
    }

    protected async unselectAll(confirmHeader: string, confirmText: string): Promise<void> {
        // Delete produktinstruktion
        if (this.valdaproduktrader.length > 0) {
            const result = await firstValueFrom(this.raskModalService.confirm(new ModalSettings(confirmHeader, confirmText)));

            if (result) {
                const events = this.valdaproduktrader.map(p => ({
                    produktId: p.produktId,
                    handelssortimentId: p.handelssortimentId,
                    produktinstruktioner: p.produktinstruktioner
                }))
                    .reduce((total, curr) => {
                        curr.produktinstruktioner.forEach(pin => {
                            total.push(new DeleteArbetsorderProduktinstruktionCommand(curr.handelssortimentId, curr.produktId, this.arbetsorderWrapper.id, pin.id));
                        });

                        return total;
                    }, []);

                // Delete produkt
                events.push(...this.valdaproduktrader
                    .filter(p => p.produktId)
                    .map(p => new DeleteArbetsorderProduktCommand(p.handelssortimentId, p.produktId, this.arbetsorderWrapper.id)));

                // Delete handelssortiment
                events.push(...toPairs(groupBy(this.valdaproduktrader, p => p.handelssortimentId))
                    .map(pair => new DeleteArbetsorderHandelssortimentCommand(Number(pair[0]), this.arbetsorderWrapper.id)));

                events.forEach(this.arbetsorderWrapper.arbetsorder.commandManager.updateCommand);
                this.valdaproduktrader = [];
                this.handelssortimentsgruppProduktvalsrader = groupBy(this.valdaproduktrader, p => p.handelssortimentsgrupp);
                this.produktsattUtfortChanged(false);
            }
        }
    }

    protected valueFormatter = (item: IProduktionsmall) => item.benamning;

    protected vardekapChanged(value: boolean) {
        this.arbetsorderWrapper.arbetsorder.vardekap = value;
        this.arbetsorderWrapper.arbetsorder.commandManager.addOrUpdate(
            new SetVardekapCommand(command => {
                command.arbetsorderId = this.arbetsorderWrapper.arbetsorder.id;
                command.vardekap = this.arbetsorderWrapper.arbetsorder.vardekap;
            }),
            (command) =>
                command.$type === SetVardekapCommand.$type &&
                command.arbetsorderId === this.arbetsorderWrapper.arbetsorder.id
        );
    }

    private applyMallProduktinstruktion(prodInstruktion: IArbetsorderProduktInstruktion, pinnarTillutbyte: any[], hs: IArbetsorderHandelssortiment, produkt: IArbetsorderProdukt) {
        if (prodInstruktion.resultatomradeIdList.some(x => x === this.arbetsorderWrapper.arbetsorder.resultatomradeId) ||
            prodInstruktion.resultatomradeIdList.length === 0) {
            if (prodInstruktion.medIUtbytessimulering) {
                pinnarTillutbyte.push(prodInstruktion.produktinstruktionId);
            }
            const produktvalEventProduktInstruktion = new ProduktvalEvent();
            produktvalEventProduktInstruktion.row = new ProduktSelection(hs.handelssortimentsgruppNamn, hs, produkt, prodInstruktion);
            produktvalEventProduktInstruktion.selectionEventType = SelectionEventType.Checked;
            this.handleSelectionEvent(produktvalEventProduktInstruktion, true);
        } else {
            produkt.produktinstruktioner.splice(produkt.produktinstruktioner.indexOf(prodInstruktion, 0), 1);
        }
    }

    private async applyProduktionsMallOnEgenSkog(mall: IProduktionsmall) {
        await this.unselectAll(this.texts.confirmHeaderMall, this.texts.confirmTextMall);
        const activeHSfromProduktionsmall = mall.handelssortiment.filter(hs => hs.aktiv);

        const pinnarTillutbyte = [];
        activeHSfromProduktionsmall.forEach(handelssortiment => {
            const produktvalEventHandelssortiment = new ProduktvalEvent();
            produktvalEventHandelssortiment.row = new ProduktSelection(handelssortiment.handelssortimentsgruppNamn, handelssortiment, null, null);
            produktvalEventHandelssortiment.selectionEventType = SelectionEventType.Checked;
            this.handleSelectionEvent(produktvalEventHandelssortiment, true);
            handelssortiment.produkter.forEach(produkt => {
                const produktvalEventProdukt = new ProduktvalEvent();
                produktvalEventProdukt.row = new ProduktSelection(handelssortiment.handelssortimentsgruppNamn, handelssortiment, produkt, null);
                produktvalEventProdukt.selectionEventType = SelectionEventType.Checked;
                this.handleSelectionEvent(produktvalEventProdukt, true);
                produkt.produktinstruktioner.forEach(produktInstruktion => {
                    this.applyMallProduktinstruktion(produktInstruktion, pinnarTillutbyte, handelssortiment, produkt);
                });
            });
        });

        this.valdaproduktrader.forEach(p => p.produktinstruktioner.forEach(y => {
            if (pinnarTillutbyte.some(x => x === y.id)) {
                p.setMedTillUtbytessimulering(y.id);
            }
        }));
        this.produktonsmallId = null;

        if (mall.vardekap === true) {
            this.vardekapChanged(mall.vardekap);
        }
    }

    private async applyProduktionsMallOnVKAO(mall: IProduktionsmall) {
        try {
            const result = await firstValueFrom(this.raskModalService
                .confirm(new ModalSettings(this.texts.confirmHeaderVirkeskopProduktMall, this.texts.confirmTextVirkeskopProduktMall)));
            if (result === ConfirmDialogResponse.save) {
                this.busyService.addWork(this.arbetsorderProduktvalService.utbytessimuleraFromVirkeskopskontraktWithProduktmall(this.arbetsorderWrapper.id, mall.id)
                    .pipe(concatMap(() => this.arbetsorderProduktvalService.checkSetProduktval(this.arbetsorderWrapper.arbetsorder))))
                    .subscribe(() => {
                        this.produktonsmallId = null;
                        this.arbetsorderMediatorService.reload();
                    });
            }
            // eslint-disable-next-line sonarjs/no-ignored-exceptions
        } catch (err) {
            return;
        }
    }

    private async checkAndConfirmMallUsage(mall: IProduktionsmall): Promise<boolean> {
        const namesOfInactiveHandelssortiment = mall.handelssortiment
            .filter(hs => !hs.aktiv)
            .map(hs => hs.namn);

        const namesOfInactiveProducts = mall.handelssortiment
            .flatMap(hs => hs.produkter)
            .filter(p => !p.aktiv)
            .map(p => p.namn);

        const namesOfInactiveProduktinstruktioner = mall.handelssortiment.flatMap(hs => hs.produkter)
            .flatMap(p => p.produktinstruktioner)
            .filter(pi => !pi.aktiv)
            .map(x => x.namn);

        const mapInactiveObjects = (names: string[], label: string): string => {
            if (names.length === 0) {
                return "";
            }
            let result = `<b>${label}</b><br>`;
            result += names.map(x => `&nbsp;&nbsp;&nbsp;${x}`).join("<br>");
            result += "<br><br>";
            return result;
        };

        let validationResult = mapInactiveObjects(namesOfInactiveHandelssortiment, "Följande handelssortiment är inaktiva:");
        validationResult += mapInactiveObjects(namesOfInactiveProducts, "Följande produkter är inaktiva:");
        validationResult += mapInactiveObjects(namesOfInactiveProduktinstruktioner, "Följande produktinstruktioner är inaktiva:");

        if (validationResult) {
            validationResult += "<br>Vill du använda mallen ändå?";
            const settings = new ModalSettings("Denna mall innehåller inaktiva element", validationResult);
            settings.contentIsMarkup = true;
            return new Promise<boolean>((resolve, reject) => {
                this.raskModalService.confirm(settings)
                    .subscribe(
                        {
                            next: (result) => {
                                if (result === ConfirmDialogResponse.save) {
                                    resolve(true);
                                } else {
                                    resolve(false);
                                }
                            },
                            error: () => resolve(false)
                        });
            });
        }

        return true;
    }

    private fetchHandelssortimentsgrupper(): void {
        this.busyService.addWork(
            this.arbetsorderProduktvalService.getHandelssortimentsgrupper(this.arbetsorderWrapper?.arbetsorder.id, dateUtils.getStartOfMonth(), dateUtils.getStartOfNextMonth())
        ).subscribe(result => {
            this.handelssortimentsgrupper = result;
        });
    }

    private handelssortimentComparer(hs: IHandelssortiment, prod?: IProdukt):
        (value: IProduktvalRow, index: number, array: IProduktvalRow[]) => boolean {
        return (value: IProduktvalRow, index: number, array: IProduktvalRow[]) => hs.handelssortimentId === value.handelssortimentId &&
            (!value.produktId || (prod !== null && prod !== undefined && value.produktId === prod.produktId));
    }

    private handleChecked(
        matchedRow: IProduktvalRow,
        prod: IProdukt,
        anyHs: boolean,
        hs: IHandelssortiment,
        $event: ProduktvalEvent,
        pin: IProduktInstruktion,
        isMallEvent: boolean) {
        if ((!matchedRow && prod) || (!prod && !anyHs)) {
            matchedRow = this.arbetsorderProduktvalService.produktvalRowFromHandelssortiment(this.arbetsorderWrapper, hs);
            this.valdaproduktrader.push(matchedRow);
        }

        if (prod) {
            matchedRow.produktId = prod.produktId;
            matchedRow.produkt = prod.namn;
            this.laggTillEjfordeladKvantitetVk($event, prod);
            if (prod.kvantitet) {
                matchedRow.setJusteradKvantitet(prod.kvantitet);
            }
        }

        if (pin) {
            if (!this.isProduktinstruktionSet(matchedRow.handelssortimentId, matchedRow.produktId) && !isMallEvent) {
                matchedRow.setMedTillUtbytessimulering(pin.produktinstruktionId);
            }

            const pinne = new Produktinstruktion(pin.namn, pin.produktinstruktionId, pin.puid);
            matchedRow.produktinstruktioner.push(pinne);
        }
        return matchedRow;
    }

    private handleSelectionEvent($event: ProduktvalEvent, isMallEvent: boolean): void {
        const hs = $event.row.handelssortiment;
        const prod = $event.row.produkt;
        const pin = $event.row.produktinstruktion;

        hs.handelssortimentsgruppNamn = $event.row.handelssortimentsgruppNamn;

        const rowsForHandelssortiment = this.valdaproduktrader
            .filter(x => x.handelssortimentId === hs.handelssortimentId);
        const matchedRow = this.valdaproduktrader.filter(this.handelssortimentComparer(hs, prod))[0];

        const anyHs = rowsForHandelssortiment.length > 0;

        if ($event.selectionEventType === SelectionEventType.Checked) {
            this.handleChecked(matchedRow, prod, anyHs, hs, $event, pin, isMallEvent);
        } else if ($event.selectionEventType === SelectionEventType.Unchecked) {
            this.handleUnchecked(matchedRow, pin, isMallEvent, prod, rowsForHandelssortiment, anyHs, $event);
        }
        this.triggerCommandFromEvent($event);
        this.setHandelssortimentsgruppProduktvalsraderFromValdaProduktrader();
        this.produktsattUtfortChanged(false);
    }

    private handleUnchecked(matchedRow: IProduktvalRow,
        pin: IProduktInstruktion,
        isMallEvent: boolean,
        prod: IProdukt,
        rowsForHandelssortiment: IProduktvalRow[],
        anyHs: boolean,
        $event: ProduktvalEvent) {
        if (matchedRow) {
            if (pin) {
                this.handleUncheckedPin(matchedRow, pin, isMallEvent);
            } else if (prod && rowsForHandelssortiment.length === 1) {
                // I am the last of my kind -> reset everything related to produkt but keep the row
                matchedRow.unsetProdukt();
            } else {
                const index = this.valdaproduktrader.indexOf(matchedRow);
                this.valdaproduktrader.splice(index, 1);
            }
        } else if (anyHs && !$event.row.produkt) {
            // Tagit bort helt handelssortiment
            remove(this.valdaproduktrader, p => p.handelssortimentId === $event.row.handelssortiment.handelssortimentId);
        }
    }

    private handleUncheckedPin(matchedRow: IProduktvalRow, pin: IProduktInstruktion, isMallEvent: boolean) {
        const pinne = matchedRow.produktinstruktioner.filter(g => g.id === pin.produktinstruktionId)[0];

        if (matchedRow.medTillUtbytessimulering === pinne.id && !isMallEvent) {
            matchedRow.setMedTillUtbytessimulering(undefined);
        }

        const index = matchedRow.produktinstruktioner.indexOf(pinne);
        matchedRow.produktinstruktioner.splice(index, 1);

        // sätt nästa pin till utbytessimulering om man tagit bort den som tidigare var vald
        if (matchedRow.produktinstruktioner.length > 0 && !this.isProduktinstruktionSet(matchedRow.handelssortimentId, matchedRow.produktId) && !isMallEvent) {
            matchedRow.setMedTillUtbytessimulering(matchedRow.produktinstruktioner[0].id);
        }
    }

    private init(): void {
        this.arbetsorderProduktvalService.refreshproduktionsMallar();
        this.arbetsorderProduktvalService.refreshSenastSimulerad(this.arbetsorderWrapper.arbetsorder.guid);
        this.arbetsorderProduktvalService.refreshVolymerHamtade(this.arbetsorderWrapper.arbetsorder.guid);
        this.arbetsorderProduktvalService.refreshVolymerAttFordela(this.arbetsorderWrapper.arbetsorder.id);
    }

    private internalHamtaVirkeskostnader(): void {
        let work$: Observable<any>;
        if (this.arbetsorderWrapper.arbetsorder.handelssortiment.length > 0) {
            work$ = this.raskModalService.confirm(new ModalSettings("Vill du hämta handelssortiment från virkesköpskontraktet?",
                "Befintliga val av handelssortiment, produkter och produktinstruktioner i Produkt tas bort."));
        } else {
            work$ = of(ConfirmDialogResponse.save);
        }

        work$.subscribe((result) => {
            if (result === ConfirmDialogResponse.save) {
                this.busyService.addWork(this.arbetsorderProduktvalService.utbytessimuleraFromVirkeskopskontrakt(this.arbetsorderWrapper.id)
                    .pipe(concatMap(() => this.arbetsorderProduktvalService.checkSetProduktval(this.arbetsorderWrapper.arbetsorder))))
                    .subscribe(() => {
                        this.arbetsorderMediatorService.reload();
                    });
            }
        });
    }

    private isProduktinstruktionSet(handelssortimentId: number, produktId: number) {
        return this.valdaproduktrader
            .find(x => x.medTillUtbytessimulering && x.handelssortimentId === handelssortimentId && x.produktId === produktId);
    }

    private laggTillEjfordeladKvantitetVk($event: ProduktvalEvent, prod: IProdukt) {
        if (!this.arbetsorderWrapper?.arbetsorder.isVirkeskop) {
            return undefined;
        }
        const totFordelat = this.valdaproduktrader?.filter(x => x.handelssortimentsgrupp === $event.row.handelssortimentsgruppNamn)
            .reduce((sum, current) => sum + current.kvantitet, 0);
        const handelsortimentGrupp = this.handelssortimentsgrupper.find(x => x.grupp === $event.row.handelssortimentsgruppNamn);
        const gruppVolym = this.arbetsorderProduktvalService.volymAttFordela$.value?.filter(p => p.handelssortimentsgruppId === handelsortimentGrupp.gruppId)[0]?.volym;
        const volymAttFordela = gruppVolym - (totFordelat ? totFordelat : 0);
        if (volymAttFordela > 0) {
            prod.kvantitet = volymAttFordela;
        }
        return undefined;
    }

    private onAlert(messages: IMessageModel[]): void {
        const grouped = groupBy(messages, msg => msg.metadata?.destineringId);
        Object.values(grouped).forEach(messageArr => {
            messageArr.forEach(x => {
                const handelssortimentId = parseInt(x.metadata?.handelssortimentId, 10);
                const values = messageArr.filter(y => parseInt(y.metadata?.handelssortimentId, 10) === handelssortimentId).map(p => p.message).join(", ");
                const hsList = this.valdaproduktrader.filter(y => y.handelssortimentId === handelssortimentId);
                hsList.forEach(y => y.valideringsMeddelande = values);
            });
        });
    }

    private setHandelssortimentsgruppProduktvalsraderFromValdaProduktrader(): void {
        this.valdaproduktrader = sortBy(this.valdaproduktrader, p => p.handelssortimentProduktAsText);
        this.handelssortimentsgruppProduktvalsrader = groupBy(this.valdaproduktrader, p => p.handelssortimentsgrupp);
    }

    private readonly setupValidation = (): void => {
        this.monitor.alerts$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(x => {
                const applicableHandelssortimentAlerts = x.filter(s => s.metadata?.handelssortimentId);
                this.onAlert(applicableHandelssortimentAlerts);

                this.produktsattReminder = x.find(s => s.code === "produktsattreminder");
            });
    };

    private triggerCommandFromEvent($event: ProduktvalEvent) {
        if ($event.row.produkt && !$event.row.produktinstruktion) {
            let cmd: ArbetsorderProduktBaseCommand;
            if ($event.selectionEventType === SelectionEventType.Checked) {
                cmd = new AddArbetsorderProduktCommand(
                    $event.row.handelssortiment.handelssortimentId,
                    $event.row.produkt.produktId,
                    this.arbetsorderWrapper.arbetsorder.id);
            } else {
                cmd = new DeleteArbetsorderProduktCommand(
                    $event.row.handelssortiment.handelssortimentId,
                    $event.row.produkt.produktId,
                    this.arbetsorderWrapper.arbetsorder.id);
            }
            this.arbetsorderWrapper.arbetsorder.commandManager.updateCommand(cmd);
        } else if ($event.row.produkt && $event.row.produktinstruktion) {
            let cmd: ArbetsorderProduktinstruktionBaseCommand;
            if ($event.selectionEventType === SelectionEventType.Checked) {
                cmd = new AddArbetsorderProduktinstruktionCommand(
                    $event.row.handelssortiment.handelssortimentId,
                    $event.row.produkt.produktId,
                    this.arbetsorderWrapper.id,
                    $event.row.produktinstruktion.produktinstruktionId);
            } else {
                cmd = new DeleteArbetsorderProduktinstruktionCommand(
                    $event.row.handelssortiment.handelssortimentId,
                    $event.row.produkt.produktId,
                    this.arbetsorderWrapper.id,
                    $event.row.produktinstruktion.produktinstruktionId);
            }
            this.arbetsorderWrapper.arbetsorder.commandManager.updateCommand(cmd);
        } else if (!$event.row.produkt) {
            let cmd: ArbetsorderHandelssortimentBaseCommand;
            if ($event.selectionEventType === SelectionEventType.Checked) {
                cmd = new AddArbetsorderHandelssortimentCommand($event.row.handelssortiment.handelssortimentId, this.arbetsorderWrapper.arbetsorder.id);
            } else {
                cmd = new DeleteArbetsorderHandelssortimentCommand($event.row.handelssortiment.handelssortimentId, this.arbetsorderWrapper.arbetsorder.id);
            }
            this.arbetsorderWrapper.arbetsorder.commandManager.updateCommand(cmd);
        }
    }
}
